/**
 * Índice e quadros do rastreio da chuva publicados pela máquina do agendador
 * (`AmaView-dados/agendador/rastreio-chuva`).
 *
 * O pyForTraCC acompanha, de um horário do GSMaP_NOW para o outro (30 em 30
 * min), cada área contínua com pelo menos 0,1 mm/h — a mesma chuva da camada
 * "Chuva por satélite", sem download a mais na máquina. Um GeoJSON por
 * horário, com CORS `*`: contornos (`k: "c"`) e trajetórias (`k: "t"`). Sem
 * previsão: só o observado.
 */
import { lerCarimbo } from '../gsmap/api';
import { fetchWithRetry } from '../lib/fetchRetry';
import { HOUR_MS } from '../lib/time';

/** Onde a máquina publica. `VITE_RASTREIO_CHUVA_URL` aponta para outro lugar (vazio desliga a camada). */
export const BASE_RASTREIO_CHUVA = (import.meta.env.VITE_RASTREIO_CHUVA_URL ?? 'https://147.15.84.134/rastreio-chuva').replace(/\/$/, '');

/** Versão do formato: os quadros têm cache imutável no navegador. */
export const VERSAO = 'v1';

export const RASTREIO_CHUVA_ATRIBUICAO = 'Rastreio da chuva: pyForTraCC — GSMaP data by JAXA';

export interface QuadroChuva {
  /** Início da hora do GSMaP (epoch ms, UTC). */
  ini: number;
  fim: number;
  /** Meio da hora: o instante que casa com a imagem do satélite (como na camada da chuva). */
  t: number;
  /** Carimbo AAAAMMDDHHNN do início (nome do arquivo). */
  c: string;
  /** Produto rastreado: `g` ajustado por pluviômetros, `s` só satélite, null sem a informação. */
  p: 'g' | 's' | null;
  /** Áreas de chuva rastreadas. */
  n: number;
  /** Área somada (km²). */
  km2: number;
  /** Maior taxa entre elas (mm/h), ou null. */
  pmax: number | null;
}

export interface IndiceChuva {
  gerado: number | null;
  /** Limiar (mm/h) e tamanho mínimo (pontos de 0,1°) de um sistema. */
  limiar: number;
  minPontos: number;
  quadros: QuadroChuva[];
}

const numero = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** Interpreta o índice; entradas inválidas são descartadas. Lança erro só quando o arquivo inteiro não serve. */
export function lerIndice(cru: unknown): IndiceChuva {
  const m = cru as { gerado?: unknown; quadros?: unknown; limiar_mmh?: unknown; min_pontos?: unknown; duracao_min?: unknown };
  if (!m || typeof m !== 'object' || !Array.isArray(m.quadros)) throw new Error('índice do rastreio da chuva sem a lista `quadros`');
  const duracao = (numero(m.duracao_min) ?? 60) * 60_000 || HOUR_MS;
  const vistos = new Set<number>();
  const quadros: QuadroChuva[] = [];
  for (const q of m.quadros as Record<string, unknown>[]) {
    if (!q || typeof q.c !== 'string') continue;
    const ini = lerCarimbo(q.c);
    if (ini === null || vistos.has(ini)) continue;
    vistos.add(ini);
    quadros.push({
      ini,
      fim: ini + duracao,
      t: ini + duracao / 2,
      c: q.c,
      p: q.p === 'g' || q.p === 's' ? q.p : null,
      n: Math.max(0, Math.round(numero(q.n) ?? 0)),
      km2: Math.max(0, numero(q.km2) ?? 0),
      pmax: numero(q.pmax),
    });
  }
  quadros.sort((a, b) => a.ini - b.ini);
  const gerado = typeof m.gerado === 'string' ? Date.parse(m.gerado) : NaN;
  return {
    gerado: Number.isFinite(gerado) ? gerado : null,
    limiar: numero(m.limiar_mmh) ?? 0.1,
    minPontos: Math.max(1, Math.round(numero(m.min_pontos) ?? 5)),
    quadros,
  };
}

/** O índice muda a cada horário novo; `no-cache` revalida no servidor (304 quando nada mudou). */
export async function carregarIndice(signal?: AbortSignal): Promise<IndiceChuva> {
  const cru = await fetchWithRetry<unknown>(`${BASE_RASTREIO_CHUVA}/${VERSAO}/indice.json`, { cache: 'no-cache', mode: 'cors', credentials: 'omit' }, {
    signal,
    read: (r) => r.json() as Promise<unknown>,
  });
  return lerIndice(cru);
}

export function urlQuadro(c: string, base: string = BASE_RASTREIO_CHUVA): string {
  return `${base}/${VERSAO}/quadros/${c}.geojson`;
}

/** Baixa um quadro (imutável: o cache HTTP do navegador guarda). */
export async function carregarQuadro(c: string, signal?: AbortSignal): Promise<unknown> {
  return fetchWithRetry<unknown>(urlQuadro(c), { mode: 'cors', credentials: 'omit' }, {
    signal,
    read: (r) => r.json() as Promise<unknown>,
  });
}
